/**
 * Top Answer — the player sees a survey question and types guesses to reveal a
 * ranked list of answers, each tagged with a percentage. Five strikes ends it.
 *
 * Ported from iOS `TopAnswerPuzzle.swift` / `TopAnswerViewModel.swift`.
 */

export interface TopAnswer {
  canonical: string;
  percentage: number;
  /** Lower-cased match keys; always includes the canonical form. */
  acceptedVariants: string[];
}

export interface TopAnswerPuzzle {
  question: string;
  category: string;
  answers: TopAnswer[];
  maxStrikes: number;
}

/** Raw Firestore shape. */
export interface TopAnswerLevel {
  question?: string;
  category?: string;
  difficulty?: string;
  maxStrikes?: number;
  answers?: Array<{
    canonical?: string;
    answer?: string;
    percentage?: unknown;
    acceptedVariants?: string[];
    variants?: string[];
  }>;
}

function intValue(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return Math.trunc(raw);
  if (typeof raw === 'string') {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) return n;
  }
  return null;
}

/** Lower-case, trim, and strip diacritics — mirrors `TopAnswerMatcher.normalize`. */
export function normalize(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function makeAnswer(
  canonical: string,
  percentage: number,
  rawVariants: string[],
): TopAnswer {
  const variants = rawVariants.map(normalize).filter((v) => v.length > 0);
  const canon = normalize(canonical);
  // The canonical form is always accepted, so an exact typing hits the fast path.
  if (canon && !variants.includes(canon)) variants.unshift(canon);
  return {
    canonical,
    percentage,
    acceptedVariants: [...new Set(variants)],
  };
}

export function normalizePuzzle(raw: TopAnswerLevel | null): TopAnswerPuzzle | null {
  if (!raw) return null;
  const question = (raw.question ?? '').trim();
  if (!question) return null;

  const answers: TopAnswer[] = [];
  for (const dict of raw.answers ?? []) {
    const canonical = dict?.canonical ?? dict?.answer ?? '';
    if (!canonical) continue;
    answers.push(
      makeAnswer(
        canonical,
        intValue(dict?.percentage) ?? 0,
        dict?.acceptedVariants ?? dict?.variants ?? [],
      ),
    );
  }
  if (answers.length < 3) return null;

  // Sorted descending so rank 1 is always answers[0].
  answers.sort((a, b) => b.percentage - a.percentage);

  return {
    question,
    category: raw.category ?? 'General',
    answers,
    // Game rule: every round grants 5 strikes. Older published levels may carry
    // a stale maxStrikes, so the rule is forced here rather than trusted.
    maxStrikes: 5,
  };
}

/** Total possible score — often > 100, since each answer carries its own share. */
export function totalPossible(puzzle: TopAnswerPuzzle): number {
  return puzzle.answers.reduce((sum, a) => sum + a.percentage, 0);
}

/** Shorter words tolerate fewer typos; capped at 2 so long answers stay strict. */
function distanceTolerance(variant: string): number {
  if (variant.length <= 3) return 0;
  if (variant.length <= 6) return 1;
  return 2;
}

function levenshtein(a: string, b: string): number {
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

/**
 * Layered match: exact variant hit first, then closest Levenshtein match inside
 * tolerance — so "ronoldo" still lands on "ronaldo" rather than burning a strike.
 * Already-revealed answers are skipped.
 */
export function matchAnswer(
  input: string,
  answers: TopAnswer[],
  revealed: Set<number>,
): number | null {
  const needle = normalize(input);
  if (!needle) return null;

  for (let i = 0; i < answers.length; i++) {
    if (revealed.has(i)) continue;
    if (answers[i].acceptedVariants.includes(needle)) return i;
  }

  let best: { idx: number; distance: number } | null = null;
  for (let i = 0; i < answers.length; i++) {
    if (revealed.has(i)) continue;
    for (const variant of answers[i].acceptedVariants) {
      const d = levenshtein(needle, variant);
      if (d <= distanceTolerance(variant) && (best === null || d < best.distance)) {
        best = { idx: i, distance: d };
      }
    }
  }
  return best?.idx ?? null;
}

/** Re-guessing a found answer shows "already found" instead of costing a strike. */
export function isAlreadyRevealed(
  input: string,
  answers: TopAnswer[],
  revealed: Set<number>,
): boolean {
  const needle = normalize(input);
  if (!needle) return false;
  for (const idx of revealed) {
    const ans = answers[idx];
    if (ans.acceptedVariants.includes(needle)) return true;
    for (const variant of ans.acceptedVariants) {
      if (levenshtein(needle, variant) <= distanceTolerance(variant)) return true;
    }
  }
  return false;
}
